/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import type { FinVoiceVadSettings } from 'embercom/objects/ai-agent/fin-voice/fin-voice-server-events';
import type { AudioDeviceSettings, WebsocketState } from 'embercom/services/fin-voice-service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { FinVoicePipeline } from 'embercom/objects/ai-agent/fin-voice/fin-voice-pipeline-options';
import { FIN_VOICE_PIPELINE_OPTIONS } from 'embercom/objects/ai-agent/fin-voice/fin-voice-pipeline-options';
import type {
  FinVoiceProcedure,
  FinVoiceRunbook,
  WorkflowSelection,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type { FinVoiceVoiceModel } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-model-options';
import { getCartesiaFallbackVoiceName } from 'embercom/objects/ai-agent/fin-voice/fin-voice-cartesia-fallback';
import { FIN_VOICE_VOICE_MODEL_OPTIONS } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-model-options';
import type { FinVoiceVoiceLanguage } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';

interface CdaRow {
  descriptorId: string;
  value: string | number | boolean;
}

interface GroupedOption {
  heading?: string;
  items: Array<{
    value: string;
    text: string;
  }>;
}

export interface SettingsArgs {
  connectionState: WebsocketState;
  selectedNumberConfigurationPhoneNumber: string | undefined;
  hasConfigChanges: boolean;
  vadSettings: FinVoiceVadSettings;
  selectedVoice: FinVoiceVoice;
  greeting: string;
  selectedPipeline: FinVoicePipeline;
  selectedRunbook: FinVoiceRunbook;
  selectedVoiceModel: FinVoiceVoiceModel;
  selectedLanguage: FinVoiceVoiceLanguage;
  audioDeviceSettings: AudioDeviceSettings;
  canUseFinVoicePlaygroundDev: boolean;
  procedures: FinVoiceProcedure[];
  proceduresLoading: boolean;
  hasProceduresError: boolean;
  availableMicrophones: Array<{
    deviceId: string;
    groupId: string;
    kind: 'audioinput';
    label: string;
    default: boolean;
  }>;
  selectedMicrophoneId: string | undefined;
  updateVadSetting: (key: keyof FinVoiceVadSettings, value: number) => void;
  updateAudioDeviceSetting: (key: keyof AudioDeviceSettings, value: boolean) => void;
  updateVoice: (voice: FinVoiceVoice) => void;
  updateGreeting: (greeting: string) => void;
  updatePipeline: (pipeline: FinVoicePipeline) => void;
  updateRunbook: (runbook: FinVoiceRunbook) => void;
  updateVoiceModel: (voiceModel: FinVoiceVoiceModel) => void;
  updateLanguage: (language: FinVoiceVoiceLanguage) => void;
  updateProcedure: (procedure: FinVoiceProcedure) => void;
  onMicrophoneChange: (deviceId: string | undefined) => void;
  useSemanticVad: boolean;
  updateUseSemanticVad: (value: boolean) => void;
  updateEagerness: (value: string) => void;
  semanticVadSettings?: {
    type: 'semantic_vad';
    eagerness: 'low' | 'medium' | 'high' | 'auto';
  };
  conversationDataAttributes: Record<string, string | number | boolean>;
  updateConversationDataAttributes: (attrs: Record<string, string | number | boolean>) => void;
  askForCsatRating: boolean;
  updateAskForCsatRating: (value: boolean) => void;
  cartesiaSpeed: number;
  cartesiaVolume: number;
  cartesiaEmotion: string;
  updateCartesiaSpeed: (value: number) => void;
  updateCartesiaVolume: (value: number) => void;
  updateCartesiaEmotion: (value: string) => void;
  lowPassFilterCutoffHz: number;
  updateLowPassFilterCutoffHz: (value: number) => void;
  deployConfig: () => Promise<void>;
  resetConfig: () => void;
}

interface Signature {
  Args: SettingsArgs;
  Element: HTMLElement;
}

export default class Settings extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare store: Store;

  @tracked cdaRows: CdaRow[] = [];

  @tracked threshold = this.args.vadSettings.threshold;
  @tracked prefixPaddingMs = this.args.vadSettings.prefixPaddingMs;
  @tracked silenceDurationMs = this.args.vadSettings.silenceDurationMs;

  @tracked echoCancellation = this.args.audioDeviceSettings.echoCancellation;
  @tracked noiseSuppression = this.args.audioDeviceSettings.noiseSuppression;
  @tracked voiceIsolation = this.args.audioDeviceSettings.voiceIsolation;
  @tracked useSemanticVad = this.args.useSemanticVad ?? false;
  @tracked selectedEagernessOption = this.args.semanticVadSettings?.eagerness ?? 'medium';
  @tracked selectedProcedure: FinVoiceProcedure | null = null;
  @tracked askForCsatRating = this.args.askForCsatRating;
  @tracked cartesiaSpeed = this.args.cartesiaSpeed;
  @tracked cartesiaVolume = this.args.cartesiaVolume;
  @tracked cartesiaEmotion = this.args.cartesiaEmotion;
  @tracked lowPassFilterCutoffHz = this.args.lowPassFilterCutoffHz;

  get currentWorkflowSelection(): WorkflowSelection {
    // Check if a procedure is selected
    if (this.selectedProcedure) {
      return {
        type: 'procedure',
        procedureId: this.selectedProcedure.id,
      };
    }

    // Default to runbook
    return {
      type: 'runbook',
      runbook: this.args.selectedRunbook,
    };
  }

  get cartesiaFallbackVoice(): string | null {
    return getCartesiaFallbackVoiceName(this.args.selectedVoice, this.args.selectedLanguage);
  }

  get isCartesiaActive(): boolean {
    return this.args.selectedVoiceModel === 'sonic-3';
  }

  @action selectElevenLabs() {
    if (!this.isSessionConnected) {
      this.args.updateVoiceModel('eleven_turbo_v2_5');
    }
  }

  @action selectCartesia() {
    if (!this.isSessionConnected) {
      this.args.updateVoiceModel('sonic-3');
    }
  }

  get selectedPipeline() {
    return this.args.selectedPipeline;
  }

  get selectedRunbook() {
    return this.args.selectedRunbook;
  }

  get selectedVoiceModel() {
    return this.args.selectedVoiceModel;
  }

  get selectedLanguage() {
    return this.args.selectedLanguage;
  }

  get isConfigSelected() {
    return !!this.args.selectedNumberConfigurationPhoneNumber;
  }

  get selectedVoice() {
    return this.args.selectedVoice;
  }

  get greeting() {
    return this.args.greeting;
  }

  set greeting(value: string) {
    this.args.updateGreeting(value);
  }

  get pipelineOptions() {
    return FIN_VOICE_PIPELINE_OPTIONS;
  }

  get voiceModelOptions() {
    return FIN_VOICE_VOICE_MODEL_OPTIONS;
  }

  get isSessionConnected() {
    return this.args.connectionState !== 'disconnected';
  }

  get emotionPresetOptions() {
    return [
      {
        value: '',
        text: this.intl.t('ai-agent.voice-playground.settings.cartesia-emotion-none'),
      },
      {
        value: 'positivity:high',
        text: this.intl.t('ai-agent.voice-playground.settings.cartesia-emotion-positive'),
      },
      {
        value: 'sadness:low',
        text: this.intl.t('ai-agent.voice-playground.settings.cartesia-emotion-empathetic'),
      },
      {
        value: 'curiosity',
        text: this.intl.t('ai-agent.voice-playground.settings.cartesia-emotion-curious'),
      },
      {
        value: 'surprise:high',
        text: this.intl.t('ai-agent.voice-playground.settings.cartesia-emotion-surprised'),
      },
      {
        value: 'anger:low',
        text: this.intl.t('ai-agent.voice-playground.settings.cartesia-emotion-firm'),
      },
    ];
  }

  get eagernessOptions() {
    return [
      { value: 'low', text: this.intl.t('ai-agent.voice-playground.settings.low') },
      { value: 'medium', text: this.intl.t('ai-agent.voice-playground.settings.medium') },
      { value: 'high', text: this.intl.t('ai-agent.voice-playground.settings.high') },
      { value: 'auto', text: this.intl.t('ai-agent.voice-playground.settings.auto') },
    ];
  }

  get microphoneOptions() {
    let options = (this.args.availableMicrophones ?? [])
      .filter((device) => device.deviceId) // Filter out devices without a valid deviceId
      .map((device) => ({
        value: device.deviceId,
        text: device.label || device.deviceId,
      }));

    // Add a "System Default" option at the beginning if not already present
    if (!options.some((opt) => opt.value === 'default')) {
      options.unshift({
        value: 'default',
        text: this.intl.t('ai-agent.voice-playground.settings.system-default-microphone'),
      });
    }

    return options;
  }

  get selectedMicrophoneId() {
    return this.args.selectedMicrophoneId || 'default';
  }

  get procedureGroupedOptions(): GroupedOption[] {
    return [
      {
        items: this.args.procedures.map((procedure) => ({
          value: procedure.id,
          text: procedure.name,
        })),
      },
    ];
  }

  get selectedProcedureId(): string | undefined {
    return this.selectedProcedure?.id;
  }

  @action onVoiceLanguageChange(voice: FinVoiceVoice, language: FinVoiceVoiceLanguage) {
    this.args.updateVoice(voice);
    this.args.updateLanguage(language);
  }

  @action onPipelineChange(event: FinVoicePipeline) {
    this.args.updatePipeline(event);
  }

  @action onRunbookChange(event: FinVoiceRunbook) {
    this.args.updateRunbook(event);
  }

  @action onVoiceModelChange(event: FinVoiceVoiceModel) {
    this.args.updateVoiceModel(event);
  }

  @action onThresholdChange(event: Event) {
    this.threshold = parseFloat((event.target as HTMLInputElement).value);
    this.args.updateVadSetting('threshold', this.threshold);
  }

  @action onPrefixPaddingMsChange(event: Event) {
    this.prefixPaddingMs = parseFloat((event.target as HTMLInputElement).value);
    this.args.updateVadSetting('prefixPaddingMs', this.prefixPaddingMs);
  }

  @action onSilenceDurationMsChange(event: Event) {
    this.silenceDurationMs = parseFloat((event.target as HTMLInputElement).value);
    this.args.updateVadSetting('silenceDurationMs', this.silenceDurationMs);
  }

  @action toggleEchoCancellation() {
    this.echoCancellation = !this.echoCancellation;
    this.args.updateAudioDeviceSetting('echoCancellation', this.echoCancellation);
  }

  @action toggleNoiseSuppression() {
    this.noiseSuppression = !this.noiseSuppression;
    this.args.updateAudioDeviceSetting('noiseSuppression', this.noiseSuppression);
  }

  @action toggleVoiceIsolation() {
    this.voiceIsolation = !this.voiceIsolation;
    this.args.updateAudioDeviceSetting('voiceIsolation', this.voiceIsolation);
  }

  @action toggleAskForCsatRating() {
    this.askForCsatRating = !this.askForCsatRating;
    this.args.updateAskForCsatRating(this.askForCsatRating);
  }

  @action toggleVadType() {
    this.useSemanticVad = !this.useSemanticVad;
    this.args.updateUseSemanticVad(this.useSemanticVad);
  }

  @action onEagernessChange(option: string) {
    this.selectedEagernessOption = option as 'low' | 'medium' | 'high' | 'auto';
    this.args.updateEagerness(option);
  }

  @action onProcedureChange(procedureId: string) {
    let procedure = this.args.procedures.find((p) => p.id === procedureId);
    if (procedure) {
      this.selectedProcedure = procedure;
      this.args.updateProcedure(procedure);
    }
  }

  @action onWorkflowChange(selection: WorkflowSelection) {
    if (selection.type === 'procedure') {
      let procedure = this.args.procedures.find((p) => p.id === selection.procedureId);
      if (procedure) {
        this.selectedProcedure = procedure;
        this.args.updateProcedure(procedure);
      }
    } else {
      this.selectedProcedure = null;
      this.args.updateRunbook(selection.runbook || 'none');
    }
  }

  @action onCartesiaSpeedChange(event: Event) {
    this.cartesiaSpeed = parseFloat((event.target as HTMLInputElement).value);
    this.args.updateCartesiaSpeed(this.cartesiaSpeed);
  }

  @action onCartesiaVolumeChange(event: Event) {
    this.cartesiaVolume = parseFloat((event.target as HTMLInputElement).value);
    this.args.updateCartesiaVolume(this.cartesiaVolume);
  }

  @action onCartesiaEmotionChange(value: string) {
    this.cartesiaEmotion = value;
    this.args.updateCartesiaEmotion(this.cartesiaEmotion);
  }

  @action onLowPassFilterCutoffHzChange(event: Event) {
    this.lowPassFilterCutoffHz = parseFloat((event.target as HTMLInputElement).value);
    this.args.updateLowPassFilterCutoffHz(this.lowPassFilterCutoffHz);
  }

  @action onMicrophoneChange(deviceId: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'change_microphone',
      microphone_device_id: deviceId === 'default' ? 'default' : deviceId,
    });
    this.args.onMicrophoneChange(deviceId === 'default' ? undefined : deviceId);
  }

  // --- Conversation Data Attributes ---

  get availableDescriptors() {
    let descriptors = ConversationAttributeDescriptor.peekAllAndMaybeLoad();
    if (!descriptors) {
      return [];
    }
    return descriptors.filter(
      (d: ConversationAttributeDescriptor) =>
        !d.archived && d.category === 'conversation' && !d.isTicketDescriptor,
    );
  }

  get unselectedDescriptors() {
    let selectedIds = new Set(this.cdaRows.map((r) => r.descriptorId));
    return this.availableDescriptors.filter(
      (d: ConversationAttributeDescriptor) => !selectedIds.has(d.id),
    );
  }

  get descriptorSelectOptions() {
    return this.unselectedDescriptors.map((d: ConversationAttributeDescriptor) => ({
      value: d.id,
      text: d.displayName,
    }));
  }

  get booleanOptions() {
    return [
      {
        value: 'true',
        text: this.intl.t('ai-agent.voice-playground.settings.cda-true'),
      },
      {
        value: 'false',
        text: this.intl.t('ai-agent.voice-playground.settings.cda-false'),
      },
    ];
  }

  getDescriptorById = (descriptorId: string): ConversationAttributeDescriptor | undefined => {
    return this.store.peekRecord('conversation-attributes/descriptor', descriptorId) ?? undefined;
  };

  getDescriptorName = (descriptorId: string): string => {
    return this.getDescriptorById(descriptorId)?.displayName ?? descriptorId;
  };

  getDescriptorDataType = (descriptorId: string): string => {
    return this.getDescriptorById(descriptorId)?.dataType ?? 'string';
  };

  isNumericType = (descriptorId: string): boolean => {
    let dataType = this.getDescriptorDataType(descriptorId);
    return dataType === 'integer' || dataType === 'decimal';
  };

  getListOptionsFor = (descriptorId: string) => {
    let descriptor = this.getDescriptorById(descriptorId);
    if (!descriptor || !descriptor.unArchivedListOptions) {
      return [];
    }
    return descriptor.unArchivedListOptions.map(
      (option: { listOptionId: string; label: string }) => ({
        value: option.listOptionId,
        text: option.label,
      }),
    );
  };

  _syncCdaToParent() {
    let attrs: Record<string, string | number | boolean> = {};
    for (let row of this.cdaRows) {
      let descriptor = this.getDescriptorById(row.descriptorId);
      if (descriptor && row.value !== '') {
        let value = row.value;
        if (this.isNumericType(row.descriptorId) && typeof value === 'string') {
          value = parseFloat(value);
        }
        if (this.getDescriptorDataType(row.descriptorId) === 'boolean') {
          value = value === 'true' || value === true;
        }
        attrs[descriptor.identifier] = value;
      }
    }
    this.args.updateConversationDataAttributes(attrs);
  }

  @action addCdaRow(descriptorId: string) {
    let dataType = this.getDescriptorDataType(descriptorId);
    let defaultValue: string | number | boolean = '';
    if (dataType === 'boolean') {
      defaultValue = 'true';
    } else if (dataType === 'integer' || dataType === 'decimal') {
      defaultValue = 0;
    } else if (dataType === 'list') {
      let options = this.getListOptionsFor(descriptorId);
      defaultValue = options.length > 0 ? options[0].value : '';
    }
    this.cdaRows = [...this.cdaRows, { descriptorId, value: defaultValue }];
    this._syncCdaToParent();
  }

  @action updateCdaRowValue(index: number, value: string | number | boolean) {
    this.cdaRows = this.cdaRows.map((row, i) => (i === index ? { ...row, value } : row));
    this._syncCdaToParent();
  }

  @action onCdaInputChange(index: number, event: Event) {
    let value = (event.target as HTMLInputElement).value;
    this.cdaRows[index].value = value;
    this._syncCdaToParent();
  }

  @action removeCdaRow(index: number) {
    this.cdaRows = this.cdaRows.filter((_, i) => i !== index);
    this._syncCdaToParent();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground::Settings': typeof Settings;
    'ai-agent/voice-playground/settings': typeof Settings;
  }
}
